import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Eye,
  Trash2,
  Download,
  FileText,
  Calendar,
  DollarSign,
  Users,
  Target,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  MoreHorizontal,
  RefreshCw,
  Settings,
  Building,
  Briefcase,
  Shield,
  Zap,
  TrendingUp,
  TrendingDown,
  Copy,
  Archive,
  Star,
  Tag,
  Globe,
  Database,
  Layers,
  Workflow,
  Code,
  HelpCircle,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import MasterDataService from '../../services/masterDataService';
import API_CONFIG from '../../config/api';

// Custom Dropdown Component
const ActionDropdown = ({
  contractType,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      // Check if there's enough space below the button
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 180; // Approximate height of the dropdown menu

      // If not enough space below, open upward
      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (contractType && contractType.id) {
      onView(contractType);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (contractType && contractType.id) {
      onEdit(contractType);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (contractType && contractType.id) {
      onDelete(contractType);
    }
  };

  const handleDuplicate = () => {
    setIsOpen(false);
    if (contractType && contractType.id) {
      onDuplicate(contractType);
    }
  };

  return (
    <div
      className="relative"
      ref={dropdownRef}
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        type="button"
        onClick={handleToggle}
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} className="text-blue-500" />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${
                  window.innerHeight -
                  buttonRef.current.getBoundingClientRect().top +
                  8
                }px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleView}
              >
                <Eye size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                View
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleEdit}
              >
                <Edit
                  size={16}
                  className="mr-2.5 text-gray-500 flex-shrink-0"
                />
                Edit
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleDuplicate}
              >
                <Copy
                  size={16}
                  className="mr-2.5 text-gray-500 flex-shrink-0"
                />
                Duplicate
              </button>
            </li>
            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 bg-transparent hover:bg-red-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleDelete}
              >
                <Trash2
                  size={16}
                  className="mr-2.5 text-red-600 flex-shrink-0"
                />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const ContractTypeManagement = ({ onNavigate }) => {
  const navigate = useNavigate();
  // helper that prefers an injected onNavigate prop but falls back to react-router navigate
  const navigateTo = (path, options) => {
    try {
      if (typeof onNavigate === 'function') {
        onNavigate(path, options);
      } else {
        // react-router navigate supports (to, { replace, state }) - pass options through if provided
        navigate(path, options);
      }
    } catch (err) {
      console.error('Navigation error:', err);
    }
  };
  const { success, error: showError, toasts, removeToast } = useToast();

  // Add CSS animations for spinning icon
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // State management
  const [contractTypes, setContractTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractTypeToDelete, setContractTypeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState({
    totalTypes: 0,
    activeTypes: 0,
    fixedPriceTypes: 0,
    timeMaterialsTypes: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Load contract types on component mount
  useEffect(() => {
    loadContractTypes();
  }, []);

  const loadContractTypes = async () => {
    setLoading(true);
    try {
      // Fetch contract types from backend
      const response = await MasterDataService.getByCategory('ContractType');
      const masterDataItems = response.data || [];
      
      // Filter out invalid contract types (those without proper labels or codes)
      // Group by label/code to identify duplicates
      const contractTypeMap = new Map();
      const duplicateIds = new Set();
      
      masterDataItems.forEach((item) => {
        const label = (item.label || '').trim();
        const code = (item.code || '').trim();
        
        // Generate fallback name from ID if label and code are both missing
        // This ensures we can still display and edit contract types that might be missing proper names
        let displayName = label || code;
        if (!displayName) {
          // Try to generate a name from the ID
          const id = item._id || item.id;
          if (id) {
            // Extract meaningful part from ObjectId or use first 8 chars
            const idStr = String(id);
            displayName = `Contract Type ${idStr.substring(idStr.length - 8)}`;
          } else {
            displayName = 'Unnamed Contract Type';
          }
        }
        
        // Use label as primary key, fallback to code, then fallback to generated name
        const key = label || code || displayName;
        
        // Check for duplicates
        if (contractTypeMap.has(key)) {
          // Mark both as duplicates - keep the first one (older)
          const existing = contractTypeMap.get(key);
          const existingDate = existing.createdAt || existing.updatedAt || new Date(0);
          const currentDate = item.createdAt || item.updatedAt || new Date(0);
          
          if (currentDate < existingDate) {
            // Current item is older, mark existing as duplicate
            duplicateIds.add(existing._id || existing.id);
            contractTypeMap.delete(key);
            contractTypeMap.set(key, item);
          } else {
            // Current item is newer, mark it as duplicate
            duplicateIds.add(item._id || item.id);
          }
        } else {
          contractTypeMap.set(key, item);
        }
      });
      
      // Transform MasterData format to component format, excluding duplicates
      const transformedTypes = Array.from(contractTypeMap.values())
        .filter(item => !duplicateIds.has(item._id || item.id))
        .map((item) => {
          // Extract metadata if available (metadata is a Map in Mongoose, convert to object)
          let metadata = {};
          if (item.metadata) {
            if (item.metadata instanceof Map) {
              metadata = Object.fromEntries(item.metadata);
            } else if (typeof item.metadata === 'object') {
              metadata = item.metadata;
            }
          }
          
          const pricingModel = metadata.pricingModel || 'Fixed-Price';
          const category = metadata.category || item.parentValue || item.parentCategory || 'Other';
          
          // Format dates safely
          const formatDate = (date) => {
            if (!date) return null;
            try {
              const dateObj = typeof date === 'string' ? new Date(date) : date;
              if (isNaN(dateObj.getTime())) return null;
              return dateObj.toISOString().split('T')[0];
            } catch (e) {
              return null;
            }
          };
          
          // Get a valid date string or use current date
          const lastUsedDate = formatDate(item.updatedAt || item.createdAt) || new Date().toISOString().split('T')[0];
          const createdAtDate = formatDate(item.createdAt) || new Date().toISOString().split('T')[0];
          
          // Ensure we always have a valid name - generate fallback if needed
          let name = (item.label || item.code || '').trim();
          if (!name) {
            // Generate a fallback name from the ID to ensure we can display and edit it
            const id = item._id || item.id;
            if (id) {
              const idStr = String(id);
              name = `Contract Type ${idStr.substring(idStr.length - 8)}`;
            } else {
              name = 'Unnamed Contract Type';
            }
            console.warn('Contract type missing label and code, using generated name:', name, 'for ID:', id);
          }
          
          return {
            id: item._id || item.id,
            _id: item._id || item.id,
            name: name,
            description: item.description || '',
            type: item.code || '',
            category: category,
            status: item.isActive !== false ? 'active' : 'inactive',
            pricingModel: pricingModel,
            baseAmount: metadata.baseAmount || 0,
            currency: metadata.currency || 'USD',
            duration: metadata.duration || '',
            approvalLevel: metadata.approvalLevel || 'Manager',
            requiresLegalReview: metadata.requiresLegalReview || false,
            requiresFinanceReview: metadata.requiresFinanceReview || false,
            autoRenewal: metadata.autoRenewal || false,
            noticePeriod: metadata.noticePeriod || 30,
            templates: metadata.templates || 0,
            contracts: metadata.contracts || 0,
            lastUsed: lastUsedDate,
            createdBy: 'System',
            createdAt: createdAtDate,
            tags: Array.isArray(metadata.tags) ? metadata.tags : [],
            features: Array.isArray(metadata.features) ? metadata.features : [],
            complianceRequirements: Array.isArray(metadata.complianceRequirements) ? metadata.complianceRequirements : []
          };
        })
        .filter(item => item !== null); // Remove null entries
      
      // Log duplicates for debugging
      if (duplicateIds.size > 0) {
        console.warn(`Found ${duplicateIds.size} duplicate contract type(s) that were filtered out`);
      }
      
      setContractTypes(transformedTypes);
      setPagination((prev) => ({
        ...prev,
        totalItems: transformedTypes.length,
        totalPages: Math.ceil(transformedTypes.length / prev.itemsPerPage),
      }));

      // Calculate stats from actual data
      setStats({
        totalTypes: transformedTypes.length,
        activeTypes: transformedTypes.filter((ct) => ct.status === 'active')
          .length,
        fixedPriceTypes: transformedTypes.filter(
          (ct) => ct.pricingModel === 'Fixed-Price'
        ).length,
        timeMaterialsTypes: transformedTypes.filter(
          (ct) => ct.pricingModel === 'Time & Materials'
        ).length,
      });
    } catch (err) {
      console.error('Error loading contract types:', err);
      showError(err.message || 'Failed to load contract types');
      setContractTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContractType = () => {
    navigateTo('/contracts/types/create');
  };

  const handleRefresh = () => {
    loadContractTypes();
  };

  // Header actions - defined after handlers
  const headerActions = [
    {
      label: '', // Icon-only button
      icon: <HelpCircle size={18} />,
      onClick: () => {
        window.open('/help/contracts/contract-types.html', '_blank');
      },
      variant: 'outline',
      tooltip: 'Contract Type Management Help',
    },
    {
      label: '', // Icon-only button
      icon: (
        <RefreshCw
          size={18}
          className={loading ? 'spinning' : ''}
          style={loading ? { animation: 'spin 1s linear infinite' } : {}}
        />
      ),
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh contract types',
      disabled: loading,
    },
    {
      label: 'New Contract Type',
      icon: <Plus size={18} />,
      onClick: handleCreateContractType,
      variant: 'primary',
      tooltip: 'Create a new contract type',
    },
  ];


  const handleEditContractType = (contractType) => {
    if (!contractType || !contractType.id) {
      showError('Invalid contract type. Cannot edit.');
      return;
    }
    navigateTo(`/contracts/types/configure/${contractType.id}?mode=edit`);
  };

  const handleViewContractType = (contractType) => {
    if (!contractType || !contractType.id) {
      showError('Invalid contract type. Cannot view.');
      return;
    }
    navigateTo(`/contracts/types/configure/${contractType.id}?mode=view`);
  };

  const handleDeleteContractType = (contractType) => {
    setContractTypeToDelete(contractType);
    setShowDeleteModal(true);
  };

  const handleDuplicateContractType = (contractType) => {
    navigateTo(`/contracts/types/create?duplicate=${contractType.id}`);
  };

  const confirmDelete = async () => {
    if (!contractTypeToDelete) return;

    setIsDeleting(true);
    try {
      // Use _id if available, otherwise fall back to id
      const contractTypeId =
        contractTypeToDelete._id || contractTypeToDelete.id;
      if (!contractTypeId) {
        throw new Error('Contract type ID is missing. Cannot delete.');
      }

      const response = await MasterDataService.delete(contractTypeId);
      if (response.success) {
        success('Contract type deleted successfully!');
        await loadContractTypes();
      } else {
        throw new Error(response.message || 'Failed to delete contract type');
      }
    } catch (err) {
      console.error('Error deleting contract type:', err);
      showError(err.message || 'Failed to delete contract type');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setContractTypeToDelete(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#10b981', bg: '#d1fae5', text: 'Active' },
      inactive: { color: '#6b7280', bg: '#f3f4f6', text: 'Inactive' },
      draft: { color: '#f59e0b', bg: '#fef3c7', text: 'Draft' },
      archived: { color: '#6b7280', bg: '#f3f4f6', text: 'Archived' },
    };

    const config = statusConfig[status] || statusConfig.active;

    return (
      <span
        className="badge"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
          borderRadius: '12px',
          padding: '4px 8px',
          fontSize: '12px',
          fontWeight: '500',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <div
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: config.color,
          }}
        />
        {config.text}
      </span>
    );
  };

  const getPricingModelBadge = (pricingModel) => {
    const modelConfig = {
      'Fixed-Price': { color: '#3b82f6', bg: '#dbeafe', text: 'Fixed-Price' },
      'Time & Materials': { color: '#8b5cf6', bg: '#ede9fe', text: 'T&M' },
      Hybrid: { color: '#f59e0b', bg: '#fef3c7', text: 'Hybrid' },
    };

    const config = modelConfig[pricingModel] || modelConfig['Fixed-Price'];

    return (
      <span
        className="badge"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
          borderRadius: '8px',
          padding: '2px 6px',
          fontSize: '11px',
          fontWeight: '500',
        }}
      >
        {config.text}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Software Development': <Code size={16} />,
      Consulting: <Users size={16} />,
      Equipment: <Building size={16} />,
      Services: <Briefcase size={16} />,
      Software: <Database size={16} />,
      Support: <Shield size={16} />,
    };
    return iconMap[category] || <FileText size={16} />;
  };

  const columns = [
    {
      key: 'name',
      label: 'Contract Type',
      render: (value, record) => {
        // Use 'record' - it already has the 'name' and 'description' 
        // derived from your loadContractTypes logic
        const name = record.name || 'Unnamed Type';
        const description = record.description || '';

        return (
          <div className="d-flex align-items-center">
            <div
              className="me-3"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#f0f9ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #bae6fd',
                flexShrink: 0,
              }}
            >
              {/* Use the specific record's category to get the icon */}
              {getCategoryIcon(record.category)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontWeight: '600',
                  color: '#1f2937',
                  fontSize: '14px',
                  marginBottom: '2px',
                  wordBreak: 'break-word',
                }}
              >
                {name}
              </div>
              {description && (
                <div
                  style={{
                    color: '#6b7280',
                    fontSize: '12px',
                    wordBreak: 'break-word',
                  }}
                >
                  {description}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'category',
      label: 'Category',
      render: (value) => (
        <div className="d-flex align-items-center">
          <span
            style={{
              marginRight: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {getCategoryIcon(value)}
          </span>
          <span style={{ fontSize: '14px', color: '#374151' }}>
            {value || 'Other'}
          </span>
        </div>
      ),
    },
    {
      key: 'pricingModel',
      label: 'Pricing Model',
      // Update parameters to (value, record)
      render: (value, record) => getPricingModelBadge(record.pricingModel),
    },
    {
      key: 'status',
      label: 'Status',
      // Update parameters to (value, record)
      render: (value, record) => getStatusBadge(record.status),
    },
    {
      key: 'contracts',
      label: 'Usage',
      // Update parameters to (value, record)
      render: (value, record) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px' }}
          >
            {record.contracts} contracts
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {record.templates} templates
          </div>
        </div>
      ),
    },
    {
      key: 'lastUsed',
      label: 'Last Used',
      // Update parameters to (value, record)
      render: (value, record) => {
        const formatDate = (dateString) => {
          if (!dateString) return 'N/A';
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleDateString();
          } catch (e) {
            return 'N/A';
          }
        };

        return (
          <div>
            <div style={{ fontSize: '14px', color: '#374151' }}>
              {formatDate(record.lastUsed)}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              by {record.createdBy || 'System'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, record) => {
        if (!record || !record.id) return <span style={{ color: 'red' }}>Error</span>;
        return (
          <div className="flex justify-start items-center">
            <ActionDropdown
              contractType={record}
              onView={handleViewContractType}
              onEdit={handleEditContractType}
              onDelete={handleDeleteContractType}
              onDuplicate={handleDuplicateContractType}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Contract Type Management"
        description="Configure and manage contract types, pricing models, and templates"
        actions={headerActions}
      />
      <div className="page-container w-full max-w-full">
        {/* KPI Cards – StatCard reference styling */}
        <div className="page-stats-grid grid-modern-4 w-full gap-6 mb-8">
          {/* Total Types */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <TrendingUp size={14} /> +0%
              </div>
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <Layers size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalTypes}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Types
                </div>
              </div>
            </div>
          </div>

          {/* Active Types */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <TrendingUp size={14} /> +0%
              </div>
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <CheckCircle size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.activeTypes}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Active Types
                </div>
              </div>
            </div>
          </div>

          {/* Fixed-Price */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <TrendingUp size={14} /> +0%
              </div>
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.fixedPriceTypes}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Fixed-Price
                </div>
              </div>
            </div>
          </div>

          {/* Time & Materials */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <TrendingUp size={14} /> +0%
              </div>
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <Clock size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.timeMaterialsTypes}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Time &amp; Materials
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contract Types Table */}
        <div className="mt-6">
          <DataTable
            data={contractTypes}
            columns={columns}
            title="Contract Types"
            loading={loading}
            pagination={pagination}
            onPaginationChange={setPagination}
            emptyMessage="No contract types found. Create your first contract type to get started."
            emptyIcon={<Layers size={48} />}
            showActions={false}
          />
        </div>

        {/* Modals */}
        <ConfirmationModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Contract Type"
          message={`Are you sure you want to delete "${contractTypeToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          loading={isDeleting}
        />

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ContractTypeManagement;
